import { queryOptions } from "@tanstack/react-query";
import { profileClient } from "@/shared/api/clients";
import type { OfficerProfile } from "../../../shared/interfaces/OfficerProfile";
import type { ProfileResponse } from "../../../shared/interfaces/ProfileResponse";

export const profileKeys = {
  all: ["profile"] as const,
  current: () => [...profileKeys.all, "current"] as const,
};

/** The ProfileResponse → OfficerProfile mapping previously inlined in
 *  MainComponent's fetch effect. */
export function mapProfileResponse(data: ProfileResponse): OfficerProfile {
  const { user, memberships } = data;
  // A signed-in officer normally has at least one membership; guard the empty
  // case so a malformed profile degrades to a blank station instead of
  // throwing and taking down the (permanently mounted) app shell.
  const membership = memberships[0];
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    department: user.metadata.department,
    rank: user.metadata.position,
    badgeNumber: user.metadata.badge_number,
    stationName: membership?.organizationName ?? "",
    status: membership?.status ?? "",
  };
}

/**
 * The signed-in officer's profile. staleTime Infinity preserves the old
 * fetch-once-per-session semantics (the context tuple never refetched);
 * the permanently-mounted MainComponent observer keeps the entry alive, so
 * gcTime stays at a generous 30 minutes — NOT gcTime 0, which is reserved
 * for mutable per-report legal state. ProfilePage writes saved edits
 * straight into this cache via setQueryData(profileKeys.current(), ...) —
 * the exact semantics of the old setter, no refetch.
 */
export function currentProfileQuery() {
  return queryOptions<OfficerProfile>({
    queryKey: profileKeys.current(),
    queryFn: async () => {
      const response = await profileClient.get("/v1/profile");
      return mapProfileResponse(response.data as ProfileResponse);
    },
    staleTime: Infinity,
    gcTime: 30 * 60_000,
  });
}
